// Copyright, DMCA, and Canadian Notice-and-Notice policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "our-position", title: "Our position",
    body: (<>
      <p>CareerOS-AI respects the intellectual-property rights of others and expects our users to do the same. We respond to valid notices under:</p>
      {bullets([
        "The United States Digital Millennium Copyright Act (DMCA), for content that would be actionable under US copyright law.",
        "Canada's Notice-and-Notice regime under the Copyright Act (sections 41.25–41.26), for content that would be actionable under Canadian copyright law.",
        "Equivalent regimes in other jurisdictions where required by law.",
      ])}
    </>) },
  { n: 2, id: "reporting-us", title: "Reporting infringement (DMCA notice)",
    body: (<>
      <p>To submit a DMCA takedown notice, include the following:</p>
      {bullets([
        "Your physical or electronic signature.",
        "Identification of the copyrighted work claimed to be infringed.",
        "Identification of the material that is claimed to be infringing, with a URL sufficient to locate it on CareerOS-AI.",
        "Your contact information (name, address, phone, email).",
        "A statement that you have a good-faith belief that use of the material is not authorized by the copyright owner, its agent, or the law.",
        "A statement, under penalty of perjury, that the information in the notification is accurate and you are authorized to act on behalf of the copyright owner.",
      ])}
      <p className="not-prose mt-4 rounded-lg border border-border p-4 bg-card/60 font-mono text-sm leading-relaxed">
        Send to: <a className="underline" href="mailto:copyright@careeros-ai.com">copyright@careeros-ai.com</a><br />
        Alt: <a className="underline" href="mailto:support@careeros-ai.com">support@careeros-ai.com</a><br />
        Subject line: &quot;DMCA notice&quot;
      </p>
    </>) },
  { n: 3, id: "reporting-canada", title: "Reporting infringement (Canadian Notice-and-Notice)",
    body: (<>
      <p>Under Canada&apos;s Notice-and-Notice regime, copyright owners may send us a notice of alleged infringement in the form required by section 41.25 of the Copyright Act (Canada). A valid notice must include:</p>
      {bullets([
        "Name and address of the claimant.",
        "The work in respect of which infringement is claimed.",
        "The claimant&apos;s interest or right in the work.",
        "The alleged infringing material and its location on CareerOS-AI.",
        "The alleged infringing activity.",
        "The date and time of the alleged infringing activity.",
      ])}
      <p>Upon receipt of a compliant notice we will forward it to the user associated with the identified material, as required by law, and retain records of the notice for the statutory period. Forwarding a notice is not a determination that the material infringes.</p>
    </>) },
  { n: 4, id: "counter-notice", title: "Counter-notice (DMCA §512(g))",
    body: (<>
      <p>If you believe your content was removed or disabled by mistake or misidentification, you may submit a counter-notice containing:</p>
      {bullets([
        "Your physical or electronic signature.",
        "Identification of the material removed or disabled and the location where it appeared before removal.",
        "A statement, under penalty of perjury, that you have a good-faith belief the material was removed or disabled as a result of mistake or misidentification.",
        "Your name, address, phone, and email.",
        "A statement that you consent to the jurisdiction of the federal court in the district where you reside (or, if outside the US, the Ontario Superior Court of Justice, Toronto).",
        "A statement that you will accept service of process from the party who submitted the original notice.",
      ])}
      <p>Send counter-notices to <span className="font-mono">copyright@careeros-ai.com</span>.</p>
    </>) },
  { n: 5, id: "false-claims", title: "False claims",
    body: (<>
      <p>Under the DMCA, knowingly filing a materially false notice or counter-notice may result in liability for damages and attorneys&apos; fees. Canadian law similarly penalizes bad-faith notices under section 41.26(2) of the Copyright Act.</p>
      <p>Please only file a notice if you are the copyright owner or their authorized agent.</p>
    </>) },
  { n: 6, id: "repeat-infringers", title: "Repeat infringers",
    body: <p>Consistent with §512(i) of the DMCA and our <Link to="/acceptable-use">Acceptable Use Policy</Link>, we will terminate the accounts of users determined to be repeat infringers.</p> },
  { n: 7, id: "trademarks", title: "Trademarks",
    body: (<>
      <p>&quot;CareerOS&quot;, &quot;CareerOS-AI&quot;, and the CareerOS mark are trademarks of CareerOS-AI. All third-party trademarks referenced in the Service belong to their respective owners.</p>
      <p>Report suspected trademark abuse to <span className="font-mono">support@careeros-ai.com</span> with the trademark, evidence of your rights, and the specific location on CareerOS-AI.</p>
    </>) },
  { n: 8, id: "ai-outputs", title: "AI-generated content and copyright",
    body: (<>
      <p>CareerOS-AI generates content in response to your prompts and profile. Copyright in the raw AI output is complex and jurisdiction-dependent (Canadian and US law treat human authorship as a threshold requirement). You are responsible for reviewing and editing AI outputs before publishing or submitting them elsewhere, especially if a copyright interest matters to you.</p>
      <p>You may not use CareerOS-AI to reproduce or infringe third-party copyrighted works — see our <Link to="/acceptable-use">Acceptable Use Policy</Link>.</p>
    </>) },
  { n: 9, id: "changes", title: "Changes to this policy",
    body: <p>We may update this policy. Material changes are communicated on the website. The &quot;Last Updated&quot; date reflects the current version.</p> },
  { n: 10, id: "contact", title: "Contact",
    body: (<>
      <p className="not-prose rounded-lg border border-border p-4 bg-card/60 font-mono text-sm leading-relaxed">
        Copyright agent: <a className="underline" href="mailto:copyright@careeros-ai.com">copyright@careeros-ai.com</a><br />
        General support: <a className="underline" href="mailto:support@careeros-ai.com">support@careeros-ai.com</a>
      </p>
    </>) },
];

const INTRO = (
  <p>This policy explains how CareerOS-AI handles copyright and trademark complaints, including notices under the US DMCA and the Canadian Notice-and-Notice regime.</p>
);

export default function Copyright() {
  return (
    <LegalLayout
      eyebrow="Legal · Copyright"
      title="Copyright & Notice-and-Notice Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/copyright"
    />
  );
}
